// evo/rooms/EvoKidsStayFreeBadge.tsx
// Green "success" pill shown at the top of a room card (right below the room name) when any of the
// room's rates carries a CHILDREN_FREE_OFFER promotion. Mirrors EvoRoomAvailabilityAlertLabel, using
// the success tokens instead of warning. This replaces the former line-item rendering of this
// promotion (see EvoRoomPromotions), matching the DP-evolution Figma. When the promotion carries a
// description, an info icon exposes it via a tooltip that opens on hover, keyboard focus AND tap.
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Text, Tooltip } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { Icon } from '@/app/common/icons/Icon';

const SuccessPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  padding: ${({ theme }) => theme.kallisto.space.space50} ${({ theme }) => theme.kallisto.space.space100};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundSuccessSubtle};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderSuccessSubtle};
`;

const TooltipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

export function EvoKidsStayFreeBadge({ description }: { description?: string }) {
  const { t } = useTranslation();
  const label = t('vacation.oss.cards.badgets.childrenstayfree');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  // The DS Tooltip alone is hover/focus-only and never opens on touch, so we drive `open` manually
  // (hover + focus + tap) and dismiss on an outside mousedown/touchstart.
  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event: Event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  return (
    <SuccessPill>
      <Text variant="BodySmall" color="textSuccess">
        {label}
      </Text>
      {description && (
        <TooltipWrapper ref={wrapperRef}>
          <Tooltip content={description} placement="top" open={open}>
            <InfoButton
              type="button"
              aria-label={label}
              // Tap (touch) opens via onClick; hover open/close is mouse-only — on touch Chromium fires a
              // synthetic mouseleave right after the tap, which would instantly close it. onBlur / outside
              // tap dismiss on touch.
              onClick={() => setOpen(true)}
              // Esc must dismiss (WCAG 1.4.13): the DS Tooltip drives Radix in controlled mode without
              // onOpenChange, so Radix's own Escape handling is a no-op — close it here.
              onKeyDown={(event) => {
                if (event.key === 'Escape') setOpen(false);
              }}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') setOpen(true);
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === 'mouse') setOpen(false);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
            >
              <Icon category="Semantics" name="icon-circle-info" size={16} color="iconSuccess" alt="" />
            </InfoButton>
          </Tooltip>
        </TooltipWrapper>
      )}
    </SuccessPill>
  );
}
